import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { en } from './locales/en';
import { hi } from './locales/hi';
import { gu } from './locales/gu';
import { ENABLE_CONSOLE_LOGS } from '@/lib/api';

export type LanguageCode = 'en' | 'hi' | 'gu';

export interface LanguageOption {
  code: LanguageCode;
  /** English label */
  label: string;
  /** Native label (shown in switcher) */
  native: string;
}

export const LANGUAGES: LanguageOption[] = [
  { code: 'en', label: 'English', native: 'English' },
  { code: 'hi', label: 'Hindi', native: 'हिन्दी' },
  { code: 'gu', label: 'Gujarati', native: 'ગુજરાતી' },
];

const LOCALES: Record<LanguageCode, typeof en> = { en, hi, gu };
const STORAGE_KEY = 'sb_lang';

/** Resolve a dot-path (e.g. "hero.subtitle") against a locale object. */
function resolvePath(source: unknown, path: string): unknown {
  return path.split('.').reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object' && key in (acc as Record<string, unknown>)) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, source);
}

interface I18nContextValue {
  lang: LanguageCode;
  setLang: (lang: LanguageCode) => void;
  /** Translate a dot-path to a string. Falls back to English, then to the raw path. */
  t: (path: string) => string;
  /** Translate a dot-path to any value (arrays/objects) with English fallback. */
  tx: <T = unknown>(path: string) => T;
}

function makeTranslator(lang: LanguageCode) {
  const t = (path: string): string => {
    const primary = resolvePath(LOCALES[lang], path);
    if (typeof primary === 'string') return primary;
    // Fallback to English so a missing translation never blanks the UI.
    const fallback = resolvePath(en, path);
    if (typeof fallback === 'string') {
      if (lang !== 'en' && ENABLE_CONSOLE_LOGS) {
        // Warn only in debug builds — never in production console.
        console.warn(`[i18n] Missing "${lang}" translation for "${path}" — using English fallback.`);
      }
      return fallback;
    }
    if (ENABLE_CONSOLE_LOGS) {
      console.warn(`[i18n] Missing translation key "${path}".`);
    }
    return path;
  };

  const tx = <T = unknown,>(path: string): T => {
    const primary = resolvePath(LOCALES[lang], path);
    if (primary !== undefined) return primary as T;
    const fallback = resolvePath(en, path);
    if (fallback === undefined && ENABLE_CONSOLE_LOGS) {
      console.warn(`[i18n] Missing translation key "${path}".`);
    }
    return fallback as T;
  };

  return { t, tx };
}

// Default (provider-less) value — always English. This keeps components that are
// rendered outside a provider (e.g. in unit tests) fully functional in English.
const defaultTranslator = makeTranslator('en');
const I18nContext = createContext<I18nContextValue>({
  lang: 'en',
  setLang: () => undefined,
  ...defaultTranslator,
});

function readInitialLang(): LanguageCode {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY) as LanguageCode | null;
    if (saved && LANGUAGES.some((l) => l.code === saved)) return saved;
  } catch {
    // localStorage unavailable
  }
  return 'en';
}

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<LanguageCode>(() =>
    typeof window === 'undefined' ? 'en' : readInitialLang()
  );

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const setLang = useCallback((next: LanguageCode) => {
    setLangState(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // ignore persistence failure
    }
  }, []);

  const translator = makeTranslator(lang);

  return (
    <I18nContext.Provider value={{ lang, setLang, ...translator }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nContextValue {
  return useContext(I18nContext);
}
